class Deposit {
  constructor() {
    this.cart = null;

    this.isProcessingQueue = false;
    this.hasPendingUpdate = false;
    this.triggeredRefresh = false;
    this.abortController = null;

    document.addEventListener("cart:updated", this.handleCartChange.bind(this));
  }

  async getCartData() {
    return this.fetchWithAbort("/cart.js", { method: "GET" });
  }

  async handleCartChange(event) {
    // Ignore updates triggered by this class
    if (event?.detail?.isDepositUpdate) return;

    if (this.isProcessingQueue) {
      this.hasPendingUpdate = true;
      // Actively cancel the in-flight request so the queue can restart immediately
      // with the freshest cart data.
      this.abortController?.abort();
      return;
    }

    document.documentElement.classList.add("is-cart-syncing");
    this.isProcessingQueue = true;
    
    do {
      this.hasPendingUpdate = false;
      
      try {
        let cart = event?.detail?.cart || null;
        event = null; // Clear the event so subsequent retries fetch fresh data

        if (!cart) {
          cart = await this.getCartData();
        }

        if (!cart) continue;

        const depositTypesInCart = this.getDepositTypesInCart(cart);
        const determinedDepositTypes = this.determineRequiredDepositTypes(cart);

        await this.checkDepositInCart(depositTypesInCart, determinedDepositTypes);
        
      } catch (error) {
        if (error.name === "AbortError") {
          console.warn("[Deposit] Stale cart update aborted to process newer data.");
        } else {
          console.error("[Deposit] Error processing cart:", error);
          this.hasPendingUpdate = false;
        }
      }
    } while (this.hasPendingUpdate);

    // Cleanup phase
    if (!this.triggeredRefresh) {
      document.documentElement.classList.remove("is-cart-syncing");
    }
    
    this.triggeredRefresh = false;
    this.cart = null;
    this.isProcessingQueue = false;
  }

  getDepositTypesInCart(cart) {
    const map = new Map();
    cart.items.forEach((item) => {
      if (item.properties?._isDepositItem) {
        map.set(item.variant_id, item);
      }
    });
    return map;
  }

  determineRequiredDepositTypes(cart) {
    const map = new Map();

    for (const item of cart.items) {
      if (item.properties?._isDepositItem) continue;

      let depositVariantId = item?.properties?._deposit_variant_id || null;
      let depositQuantity = item?.properties?._deposit_quantity || 0;

      if (!depositVariantId) {
        const extractedData = this.checkCartLineForDeposit(item);
        depositVariantId = extractedData.depositVariantId;
        depositQuantity = extractedData.depositQuantity;

        if (!depositVariantId) continue;
      }

      let newQuantity = depositQuantity
        ? parseInt(depositQuantity) * item.quantity
        : item.quantity;

      if (map.has(depositVariantId)) {
        newQuantity += map.get(depositVariantId).totalQuantity;
      }

      map.set(depositVariantId, {
        variant_id: depositVariantId,
        totalQuantity: newQuantity,
      });
    }

    return map;
  }

  checkCartLineForDeposit(item) {
    const cartElement = document.querySelector("cart-drawer, main-cart");
    if (!cartElement) return { depositVariantId: null, depositQuantity: null };

    const productElement = cartElement.querySelector(
      `.cart-item[data-variant-id='${item.variant_id}']`
    );

    if (!productElement) return { depositVariantId: null, depositQuantity: null };

    return {
      depositVariantId: productElement.getAttribute("data-deposit-variant-id"),
      depositQuantity: productElement.getAttribute("data-deposit-quantity"),
    };
  }

  async checkDepositInCart(depositTypesInCart, determinedDepositTypes) {
    if (determinedDepositTypes.size === 0 && depositTypesInCart.size === 0) return;

    if (determinedDepositTypes.size === 0 && depositTypesInCart.size > 0) {
      await this.clearDepositItemsFromCart(depositTypesInCart);
      this.updateCartUI();
      return;
    }

    const addData = [];
    const updateData = {};

    for (const type of determinedDepositTypes.values()) {
      const variantId = type.variant_id;
      const quantity = type.totalQuantity;
      const parsedId = parseInt(variantId);

      const depositItemInCart = depositTypesInCart.get(parsedId);

      if (!depositItemInCart) {
        addData.push({
          id: parsedId,
          quantity,
          properties: { _isDepositItem: true },
        });
        continue;
      }

      if (depositItemInCart.quantity !== quantity) {
        updateData[variantId] = quantity;
      }

      depositTypesInCart.delete(parsedId);
    }

    // Anything left in the map is no longer required
    for (const type of depositTypesInCart.values()) {
      updateData[type.variant_id] = 0;
    }

    let madeChanges = false;

    if (addData.length > 0) {
      await this.addDepositToCart(addData);
      madeChanges = true;
    }

    if (Object.keys(updateData).length > 0) {
      await this.updateDepositInCart(updateData);
      madeChanges = true;
    }

    if (madeChanges) {
      this.updateCartUI();
    }
  }

  async addDepositToCart(items) {
    const data = await this.fetchWithAbort(
      (window.Shopify?.routes?.root || "/") + "cart/add.js",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      }
    );
    this.cart = data;
    return data;
  }

  async updateDepositInCart(updates) {
    const data = await this.fetchWithAbort(
      (window.Shopify?.routes?.root || "/") + "cart/update.js",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      }
    );
    this.cart = data;
    return data;
  }

  async clearDepositItemsFromCart(depositItemsMap) {
    const updateData = {};
    for (const item of depositItemsMap.values()) {
      updateData[item.variant_id] = 0;
    }
    await this.updateDepositInCart(updateData);
  }

  updateCartUI() {
    if (!this.cart) return;

    this.triggeredRefresh = true;
    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: {
          cart: this.cart,
          isDepositUpdate: true,
        },
      })
    );
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  }

  async fetchWithAbort(url, options, retries = 3, delay = 500) {
    const isGetRequest = !options || options.method === "GET";
    
    const controller = isGetRequest ? new AbortController() : null;
    if (isGetRequest) {
      this.abortController = controller;
    }

    try {
      const fetchOptions = { ...options };
      if (controller) fetchOptions.signal = controller.signal;

      const response = await fetch(url, fetchOptions);

      if (response.status === 429) {
        if (retries > 0) {
          
          await new Promise((resolve) => {
            const timeout = setTimeout(resolve, delay);
            controller.signal.addEventListener("abort", () => {
              clearTimeout(timeout);
              resolve();
            }, { once: true });
          });
          
          if (controller.signal.aborted) {
            throw new DOMException("Aborted", "AbortError");
          }
          
          return await this.fetchWithAbort(url, options, retries - 1, delay * 2);
        } else {
          throw new Error("RATE_LIMIT_EXCEEDED");
        }
      }

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status}`);
      }

      return await response.json();
    } finally {
      if (this.abortController === controller) {
        this.abortController = null;
      }
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const deposit = new Deposit();
  deposit.handleCartChange({});
});